import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { getButtonVariantProps } from 'pricing-pages-lib/utils/buttonVariantUtils';
import { PPUIGridItem } from 'pricing-pages-ui-components/grid/PPUIGridItem';
import { SectionTitle } from 'pricing-pages-ui-components/semantic/PageSection';
import SuiteProductLevelIdentifiersMap from 'pricing-pages-ui/constants/merchCards/SuiteProductLevelIdentifiersMap';
import { ENTERPRISE, FREE, PROFESSIONAL, STARTER } from 'self-service-api/constants/ProductLevels';
import { SUITE_CARD } from '../../constants/tracking/TrackingSources';
import { SuiteCTA } from '../../features/purchaseMotions/components/SuiteCTA';
import useSuitePurchaseMotions from '../../features/purchaseMotions/hooks/useSuitePurchaseMotions';
import { useGlobalLoading } from '../../hooks/loadingHooks';
import { useQueryParamTerm } from '../../utils/urls/appQueryParamUtils';
import { SuiteBadge } from '../common/badges/SuiteBadge';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import IncludedSeatsDisplay from '../merch-card/IncludedSeatsDisplay';
import SuiteTermSelectorContainer from '../merch-card/SuiteTermSelectorContainer';
import CrmSuitePriceMerchCard from '../merch-card/price-display/CrmSuitePriceMerchCard';
import { useCrmSuiteBundlePricingMetadata } from '../merch-card/price-display/useCrmSuitePricingInfo';
import MobileSuiteBusinessObjectives from './MobileSuiteBusinessObjectives';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const getStringKeysForProductLevel = productLevel => {
  switch (productLevel) {
    case FREE:
      return {
        titleKey: 'merchCards.suiteTile.freeCard.title',
        subtitleKey: 'merchCards.suiteTile.freeCard.description'
      };
    case STARTER:
      return {
        titleKey: 'merchCards.suiteTile.starterCard.title',
        subtitleKey: 'merchCards.suiteTile.starterCard.description'
      };
    case PROFESSIONAL:
      return {
        titleKey: 'merchCards.suiteTile.professionalCard.title',
        subtitleKey: 'merchCards.suiteTile.professionalCard.description'
      };
    case ENTERPRISE:
      return {
        titleKey: 'merchCards.suiteTile.enterpriseCard.title',
        subtitleKey: 'merchCards.suiteTile.enterpriseCard.description'
      };
    default:
      return {
        titleKey: '',
        subtitleKey: ''
      };
  }
};
export const MobileSuiteTile = ({
  productLevel,
  screen,
  gridItemProps
}) => {
  const isAppReady = useGlobalLoading();
  const {
    setQueryParamTerm,
    queryParamTerm
  } = useQueryParamTerm();
  const {
    titleKey,
    subtitleKey
  } = getStringKeysForProductLevel(productLevel);
  const {
    showPricePerMonthPerSeat,
    suiteSkuId
  } = useCrmSuiteBundlePricingMetadata({
    productLevel
  });
  const purchaseMotions = useSuitePurchaseMotions({
    productLevel
  });
  const ctaVariantProps = getButtonVariantProps({
    variant: 'block'
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(PPUIGridItem, Object.assign({}, gridItemProps, {
      showBorderTop: true,
      position: "relative",
      gridRowStart: 1,
      "data-selenium-id": `${SuiteProductLevelIdentifiersMap[productLevel]}-merch-card`,
      children: [/*#__PURE__*/_jsx(SuiteBadge, {
        productLevel: productLevel
      }), /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        paddingBlockStart: "600",
        alignItems: "center",
        children: /*#__PURE__*/_jsx(SectionTitle, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: titleKey
          })
        })
      })]
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "600",
      gridRowStart: 2,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: subtitleKey
      })
    })), /*#__PURE__*/_jsxs(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "400",
      gridRowStart: 3,
      children: [/*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !isAppReady,
        heights: [16, 23, 16],
        widths: [60, 70, 130],
        children: /*#__PURE__*/_jsx(Flex, {
          flexDirection: "column",
          children: /*#__PURE__*/_jsx(CrmSuitePriceMerchCard, {
            productLevel: productLevel,
            showPricePerMonthPerSeat: showPricePerMonthPerSeat
          })
        })
      }), suiteSkuId ? /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !isAppReady,
        heights: [16, 23, 16],
        widths: [60, 70, 130],
        children: /*#__PURE__*/_jsx(IncludedSeatsDisplay, {
          skuId: suiteSkuId,
          queryParamTerm: queryParamTerm
        })
      }) : null]
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "600",
      gridRowStart: 4,
      children: /*#__PURE__*/_jsx(View, {
        marginBlockStart: "300",
        children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
          isLoading: !isAppReady,
          heights: [41],
          widths: ['100%'],
          children: /*#__PURE__*/_jsx(SuiteTermSelectorContainer, {
            productLevel: productLevel,
            onChange: setQueryParamTerm,
            queryParamTerm: queryParamTerm,
            trackingSource: SUITE_CARD
          })
        })
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "400",
      gridRowStart: 5,
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !isAppReady,
        heights: [47],
        widths: ['100%'],
        children: /*#__PURE__*/_jsx(Flex, {
          flexDirection: "column",
          justifyContent: "center",
          gap: "200",
          inlineSize: "100%",
          marginBlockEnd: "700",
          children: purchaseMotions.map((purchaseMotion, index) => /*#__PURE__*/_jsx("div", {
            children: /*#__PURE__*/_jsx(SuiteCTA, Object.assign({
              use: index === 0 ? 'primary' : 'secondary',
              purchaseMotion: purchaseMotion,
              productLevel: productLevel,
              trackingSource: SUITE_CARD,
              screen: screen,
              purchaseMotionLocation: 'MERCH_CARD'
            }, ctaVariantProps))
          }, purchaseMotion))
        })
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "400",
      paddingBlockEnd: "600",
      gridRowStart: 6,
      showBorderBottom: true,
      children: /*#__PURE__*/_jsx(MobileSuiteBusinessObjectives, {
        productLevel: productLevel
      })
    }))]
  });
};